/**
 * F41 — Bracket geometry (web) — feeder-centred tree layout.
 *
 * The design-pass ladder centred each round with flex `justify-around`, which
 * silently ASSUMES every round is a balanced power-of-two half of the previous
 * one. A non-power-of-two field gives byes to the top seeds, so a round-2 match
 * may be fed by a bye seed (which has NO round-1 match) — `justify-around` then
 * spreads the round-2 column evenly and the connector "ladder" no longer lines
 * each match up between its two ACTUAL feeders.
 *
 * The robust fix is to derive each match's vertical centre from the tree itself
 * (follow `nextMatchId` wiring): a round-1 match's centre is its slot index; a
 * round-N match's centre is the AVERAGE of its feeders' centres. Bye-fed matches
 * (one or zero feeders) fall out correctly — a single-feeder match sits at its
 * feeder's centre; a feeder-less match keeps a sequential slot. The component
 * then positions every match absolutely at `centre × ROW_PITCH`, so the tree +
 * connector lines align for any field size, power-of-two or not.
 *
 * Pure + token-free + JSX-free so it unit-tests in isolation.
 */

export interface LayoutMatchLike {
  readonly id: string;
  readonly nextMatchId?: string | null;
}

export interface LayoutRoundLike<M extends LayoutMatchLike> {
  readonly id: string;
  readonly ordinal: number;
  readonly matches: ReadonlyArray<M>;
}

/** The computed vertical geometry for one match node. */
export interface MatchLayout {
  /** Vertical centre in row-pitch units (round-1 slot index space). */
  readonly center: number;
  /** Feeder match ids resolved from the `nextMatchId` wiring (0–2 normally). */
  readonly feederIds: ReadonlyArray<string>;
}

export interface BracketLayout {
  /** Per-match geometry keyed by match id. */
  readonly byMatchId: ReadonlyMap<string, MatchLayout>;
  /** The min centre across all matches (used to normalise to a 0-based top). */
  readonly minCenter: number;
  /** The max centre across all matches. */
  readonly maxCenter: number;
}

/**
 * Compute each match's tree-derived vertical centre.
 *
 * Rounds are processed in ordinal order so feeders are always positioned before
 * the matches they feed. A round-1 (lowest-ordinal) match anchors to a
 * sequential slot index; a later-round match averages its feeders' centres, and
 * falls back to a fresh slot when it has no resolvable feeders (defensive — a
 * well-formed single-elim tree always wires every non-first-round match).
 */
export function computeBracketLayout<M extends LayoutMatchLike>(
  rounds: ReadonlyArray<LayoutRoundLike<M>>,
): BracketLayout {
  const ordered = [...rounds].sort((a, b) => a.ordinal - b.ordinal);

  // Map every match id → the ids of the matches that feed it (its prior-round
  // sources). Built from the `nextMatchId` wiring across all rounds.
  const feedersByTarget = new Map<string, string[]>();
  for (const round of ordered) {
    for (const m of round.matches) {
      const next = m.nextMatchId ?? null;
      if (next) {
        const list = feedersByTarget.get(next) ?? [];
        list.push(m.id);
        feedersByTarget.set(next, list);
      }
    }
  }

  const byMatchId = new Map<string, MatchLayout>();
  // A running slot cursor for anchor matches (first round + any feeder-less node).
  let nextSlot = 0;

  let minCenter = Number.POSITIVE_INFINITY;
  let maxCenter = Number.NEGATIVE_INFINITY;

  ordered.forEach((round, roundIdx) => {
    for (const m of round.matches) {
      const feederIds = feedersByTarget.get(m.id) ?? [];
      // Only feeders that have already been positioned count (always true given
      // ordinal processing, but guards against malformed cross-round wiring).
      const placed = feederIds
        .map((id) => byMatchId.get(id)?.center)
        .filter((c): c is number => c !== undefined);

      let center: number;
      if (roundIdx === 0 || placed.length === 0) {
        // Anchor: first-round matches + any node with no resolvable feeder get
        // the next sequential slot so they never collide.
        center = nextSlot;
        nextSlot += 1;
      } else {
        center = placed.reduce((sum, c) => sum + c, 0) / placed.length;
      }

      byMatchId.set(m.id, { center, feederIds });
      if (center < minCenter) minCenter = center;
      if (center > maxCenter) maxCenter = center;
    }
  });

  if (!Number.isFinite(minCenter)) {
    minCenter = 0;
    maxCenter = 0;
  }

  return { byMatchId, minCenter, maxCenter };
}
